import { type OpeningDetailResponse } from 'api-clients/monolith';

/**
 * The address fields a recruiter edits for a remote opening. Progress counts
 * them by value, not by error: a blank one is a gap before validation ever
 * runs and after a Discard clears a failed Save's errors.
 */
export const HIRING_ADDRESS_KEYS = [
  'streetAddress',
  'city',
  'postalCode',
] as const;

export function isBlankAddressField(value: string | null | undefined): boolean {
  return !value?.trim();
}

export type HiringAddressValues = {
  streetAddress?: string | null;
  city: string | null;
  postalCode: string | null;
};

/** The address Hire holds for an opening, as the job-details query returned it. */
export function hiringAddressOf(
  opening: Pick<
    OpeningDetailResponse,
    'street_address' | 'city' | 'postal_code'
  >,
): HiringAddressValues {
  return {
    streetAddress: opening.street_address ?? undefined,
    city: opening.city,
    postalCode: opening.postal_code,
  };
}

/** Blank, null and undefined are one value: Hire stores a cleared field as null. */
export function sameAddressValue(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  return (a?.trim() || null) === (b?.trim() || null);
}

/**
 * The address a refetch gives a remote opening's card. A field the recruiter
 * has not edited since it was last loaded takes Hire's value, so a card whose
 * Location another card saved shows the saved address; an unsaved edit stays.
 * With no `loaded` baseline, as when the step remounts after Back, a field
 * that differs from Hire's value is an unsaved edit.
 */
export function refreshUntouchedAddress(
  current: HiringAddressValues,
  loaded: HiringAddressValues | undefined,
  fetched: HiringAddressValues,
): HiringAddressValues {
  const baseline = loaded ?? fetched;
  const untouched = (key: (typeof HIRING_ADDRESS_KEYS)[number]) =>
    sameAddressValue(current[key], baseline[key]);
  return {
    streetAddress: untouched('streetAddress')
      ? fetched.streetAddress
      : current.streetAddress,
    city: untouched('city') ? fetched.city : current.city,
    postalCode: untouched('postalCode')
      ? fetched.postalCode
      : current.postalCode,
  };
}
